import React, { Fragment, ReactNode } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ArrowLongLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useRouter } from 'next/navigation';
import { useOverlayUrlParamSync } from '@/ui/hooks/productDetail/useOverlayUrlParamSync';
import { useTranslations } from 'next-intl';
import { CART_PORTAL_TARGET_ID, NOTIFY_ME_PORTAL_TARGET_ID } from '@/utils/constants';
import { useIsHydrated } from '@/ui/hooks/useIsHydrated';
import { useCartStore } from '@/ui/store/cart';

export default function FullScreenOverlay({
  children,
  paramName,
  initialOpen,
}: Readonly<{
  children?: ReactNode;
  paramName: string;
  initialOpen?: boolean;
}>) {
  const t = useTranslations('FullScreenOverlay');
  const { open, closeOverlay } = useOverlayUrlParamSync(paramName, true, initialOpen);
  const isHydrated = useIsHydrated();
  const { isCartDisplayed } = useCartStore();

  const router = useRouter();

  // The cart drawer opens on top of this overlay (inside CART_PORTAL_TARGET_ID) and makes it inert,
  // freezing this Back. The cart drawer provides its own Back ("Continue Shopping" in the cart view,
  // and a dedicated Back in the upselling step), so hide this frozen one whenever the drawer is open.

  const defaultOverlayClose = () => {};

  const overlayContent = (
    <div className="flex h-screen flex-col overflow-y-auto overscroll-contain bg-white shadow-xl supports-[height:100svh]:h-[100svh] lg:overflow-y-hidden">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 w-screen bg-white px-4 py-3 sm:px-6 md:fixed lg:bg-transparent">
        <div className="flex items-center justify-between">
          {/* Back Button — hidden while the cart drawer (which provides its own Back) is open */}
          {isCartDisplayed ? (
            <span />
          ) : (
            <button type="button" className="flex items-center gap-1" onClick={() => router.back()}>
              <ArrowLongLeftIcon className="h-5 w-5 text-primary-100" />
              {t('Back')}
            </button>
          )}
          {/* Close Button */}
          <button
            type="button"
            onClick={() => closeOverlay()}
            className="flex items-center"
            aria-label={t('Close')}
          >
            <XMarkIcon className="h-5 w-5 text-neutral-50" aria-hidden="true" />{' '}
          </button>
        </div>
      </div>
      <div className="relative flex-1">
        {children}
        <div id={NOTIFY_ME_PORTAL_TARGET_ID} />
        <div id={CART_PORTAL_TARGET_ID} />
      </div>
    </div>
  );

  // Before hydration, Headless UI Dialog/Transition don't render on the server.
  // When initialOpen is true, render a static overlay so the content is in the SSR HTML.
  if (initialOpen && !isHydrated) {
    return (
      <div className="relative z-50">
        <div className="fixed inset-y-0 right-0 flex w-full">
          <div className="pointer-events-auto w-screen">{overlayContent}</div>
        </div>
      </div>
    );
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={defaultOverlayClose}>
        <div className="fixed inset-y-0 right-0 flex w-full">
          <Transition.Child
            as={Fragment}
            enter="transform transition ease-in-out duration-500 sm:duration-700"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transform transition ease-in-out duration-500 sm:duration-700"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <Dialog.Panel className="pointer-events-auto w-screen ">{overlayContent}</Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
